import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageIcon } from '@phosphor-icons/react/dist/csr/Package';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Select } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { readersApi, sensorsApi } from '../../lib/api/devices';
import { dispensariesApi, type Dispensary } from '../../lib/api/reference';
import { replacementsApi, type ReplacementRow } from '../../lib/api/replacements';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './DispenseDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * `POST /replacements/{replacement}/dispense` (M-13, Task 5, spec R-4).
 *
 * ## THIS DIALOG PICKS A REAL, REGISTERED, IN-STOCK DEVICE
 *
 * A free-text serial box here would recreate the legacy exactly: a string
 * nobody validates, and no record at all of what the patient received. The
 * legacy has no stock table, no inventory table and no quantity column
 * anywhere; marking a replacement `'completed'` wrote two columns on one row
 * and touched nothing else, which is why that system cannot answer "what
 * device does this patient have".
 *
 * The picker is a DISPENSARY first, then the devices in stock AT that
 * dispensary, of the kind this replacement is for. That order matches what
 * the server enforces: `ReplacementTransition` refuses any device whose
 * `dispensary_id` is not the fulfilling one, so offering devices from
 * anywhere would offer choices guaranteed to `422`.
 *
 * `GET /reference/dispensaries` and `GET /devices/{kind}` are both open to
 * any active account (`ReferencePolicy::viewAny()`,
 * `DevicePolicy::viewAny()` — verified against the API repository), so a
 * pharmacist holding only `replacement.fulfil` and `replacement.view` can
 * populate both lists. That was checked rather than assumed: a picker
 * reaching for an endpoint the fulfiller cannot read would make this dialog
 * 403 for exactly the role it exists for.
 *
 * ## Availability is still the server's decision
 *
 * Nothing here re-implements the stock rules. A device that went out from
 * under the caller between the list load and the submit is a `422` naming
 * the device field, and this dialog renders it against that field rather
 * than predicting it. A terminal replacement is a `409` naming `status` —
 * reachable in normal use, because two pharmacists can hold the same queue
 * open.
 */

export interface DispenseDialogProps {
  open: boolean;
  /** `null` while nothing is being acted on. The whole row, not just the uuid: the device kind and the case number both come off it. */
  replacement: ReplacementRow | null;
  onClose: () => void;
  onDone: (updated: ReplacementRow) => void;
}

interface DeviceOption {
  uuid: string;
  serial_number: string;
}

export function DispenseDialog({ open, replacement, onClose, onDone }: DispenseDialogProps) {
  const { t } = useTranslation();

  const [dispensaries, setDispensaries] = useState<readonly Dispensary[]>([]);
  const [dispensaryUuid, setDispensaryUuid] = useState('');
  const [devices, setDevices] = useState<readonly DeviceOption[]>([]);
  const [deviceUuid, setDeviceUuid] = useState('');
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [missingDevice, setMissingDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  const deviceField = replacement?.device_type === 'reader' ? 'new_reader_uuid' : 'new_sensor_uuid';

  useEffect(() => {
    if (!open) return;
    setDispensaryUuid('');
    setDevices([]);
    setDeviceUuid('');
    setMissingDevice(false);
    setFailure(null);
    setFieldErrors({});

    let cancelled = false;
    void (async () => {
      try {
        const list = await dispensariesApi.list();
        if (!cancelled) setDispensaries(list);
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        if (!cancelled) setFailure(t('replacements.dispensariesLoadFailed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, t]);

  // The device list is a function of (dispensary, kind). It reloads whenever
  // either changes, and it asks the SERVER for `status: 'in_stock'` rather
  // than fetching everything and filtering here — the same "every filter is
  // a server parameter" rule the queue follows.
  useEffect(() => {
    if (!open || replacement === null || dispensaryUuid === '') {
      setDevices([]);
      setDeviceUuid('');
      return;
    }

    let cancelled = false;
    setDevicesLoading(true);
    setDeviceUuid('');

    void (async () => {
      try {
        const page =
          replacement.device_type === 'reader'
            ? await readersApi.list({ status: 'in_stock', dispensary: dispensaryUuid, per_page: 200 })
            : await sensorsApi.list({ status: 'in_stock', dispensary: dispensaryUuid, per_page: 200 });

        if (cancelled) return;
        setDevices(page.data.map((device) => ({ uuid: device.uuid, serial_number: device.serial_number })));
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        if (!cancelled) {
          setDevices([]);
          setFailure(t('replacements.devicesLoadFailed'));
        }
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, replacement, dispensaryUuid, t]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('replacements.notPermittedToFulfil');
      case TOO_MANY_REQUESTS_CODE:
        return t('replacements.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('replacements.dispenseFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || replacement === null) return;

    if (deviceUuid === '' || dispensaryUuid === '') {
      setMissingDevice(true);
      return;
    }
    setMissingDevice(false);

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const updated = await replacementsApi.dispense(replacement.uuid, {
        // EXACTLY ONE of the two, chosen by the replacement's own kind — the
        // server's `required_without` pair. Sending both would name two
        // devices for a replacement that covers one (spec R-2).
        ...(replacement.device_type === 'reader'
          ? { new_reader_uuid: deviceUuid }
          : { new_sensor_uuid: deviceUuid }),
        fulfilling_dispensary_uuid: dispensaryUuid,
      });
      onDone(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      // A `409`'s `status` key belongs in the banner, not against the
      // device field — see this file's own docblock.
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== deviceField)
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const deviceOptions = devices.map((device) => ({ value: device.uuid, label: device.serial_number }));

  return (
    <Dialog
      open={open}
      kind="form"
      onClose={onClose}
      title={t('replacements.dispenseTitle', { caseNumber: replacement?.case.case_number ?? '' })}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Select
          label={t('replacements.fieldFulfillingDispensary')}
          width="fill"
          required
          value={dispensaryUuid}
          placeholder={t('replacements.chooseDispensary')}
          options={dispensaries.map((dispensary) => ({
            value: dispensary.uuid,
            label: dispensary.name_en,
          }))}
          error={fieldErrors['fulfilling_dispensary_uuid'] ?? undefined}
          onChange={(event) => {
            const { value } = event.target;
            setDispensaryUuid(value);
          }}
        />

        <Select
          label={t('replacements.fieldNewDevice')}
          width="fill"
          required
          disabled={dispensaryUuid === '' || devicesLoading}
          value={deviceUuid}
          placeholder={
            dispensaryUuid === ''
              ? t('replacements.chooseDispensaryFirst')
              : devicesLoading
                ? t('list.loading')
                : deviceOptions.length === 0
                  ? t('replacements.noStockHere')
                  : t('replacements.chooseDevice')
          }
          options={deviceOptions}
          error={fieldErrors[deviceField] ?? undefined}
          hint={t('replacements.deviceHint')}
          onChange={(event) => {
            const { value } = event.target;
            setDeviceUuid(value);
            setMissingDevice(false);
          }}
        />

        {missingDevice ? (
          <span className={styles['fieldError']} role="alert">
            {t('replacements.chooseDeviceRequired')}
          </span>
        ) : null}

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<PackageIcon size={16} />}>
            {t('replacements.dispenseSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('replacements.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
